import { Box, Stack, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { useDsarExport } from './useDsarExport';
import type { DsarExportPackage } from './dsarExportModel';

/**
 * The assembled DSAR data package (D-dsar). A READ-ONLY view of the member's OWN data, the
 * fulfilment deliverable in the mock (a real file export is the migration seam). It renders the
 * full admin-side record of the member's own data: the profile, transactions, cashouts
 * INCLUDING the member's payout details, the balance ledger, clicks, tickets, welcome bonuses
 * and the referral edge. The internal operational records ABOUT the member (audit entries,
 * impersonation sessions viewing them) are not their personal data, so they are never assembled
 * here. Promo redemptions are not modelled per member, so the package says so honestly rather
 * than inventing a list.
 */

const day = (s: string): string => s.slice(0, 10);

const Section = ({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ mb: 2 }} data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Empty = ({ label }: { label: string }) => (
  <Typography variant="body2" color="text.secondary">
    {label}
  </Typography>
);

const PackageBody = ({ pkg }: { pkg: DsarExportPackage }) => (
  <Box
    data-testid="dsar-package"
    sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      The member's own data as we hold it, assembled for release. A downloadable
      file is produced by the real data API; here the assembled record is the
      deliverable.
    </Typography>

    <Section title="Profile" testId="dsar-package-profile">
      <Typography variant="body2" data-testid="dsar-package-profile-line">
        {pkg.profile.name} | {pkg.profile.email} |{' '}
        {pkg.profile.market.toUpperCase()} | {pkg.profile.tier} | joined{' '}
        {day(pkg.profile.createdAt)}
      </Typography>
    </Section>

    <Section title="Transactions" testId="dsar-package-transactions">
      {pkg.transactions.length === 0 ? (
        <Empty label="No transactions held." />
      ) : (
        <Stack spacing={0.5}>
          {pkg.transactions.map((t) => (
            <Typography key={t.id} variant="body2">
              #{t.id} | {t.grossAmount} order | {t.cashbackAmount} cashback |{' '}
              {t.status} | {day(t.occurredAt)}
            </Typography>
          ))}
        </Stack>
      )}
    </Section>

    <Section title="Cashouts" testId="dsar-package-cashouts">
      {pkg.cashouts.length === 0 ? (
        <Empty label="No cashouts held." />
      ) : (
        <Stack spacing={0.5}>
          {pkg.cashouts.map((c) => (
            <Typography key={c.id} variant="body2">
              #{c.id} | {c.amount} | {c.destinationType} |{' '}
              {c.payoutMethod ?? 'donation'} | {c.status} |{' '}
              {c.payoutDetails
                ? `payout details ${c.payoutDetails}`
                : 'no payout details'}
            </Typography>
          ))}
        </Stack>
      )}
    </Section>

    <Section title="Balance ledger" testId="dsar-package-ledger">
      {pkg.ledgerEntries.length === 0 ? (
        <Empty label="No ledger entries held." />
      ) : (
        <Stack spacing={0.5}>
          {pkg.ledgerEntries.map((e) => (
            <Typography key={e.id} variant="body2">
              #{e.id} | {e.amount} | {e.kind} | {e.reason} | {day(e.createdAt)}
            </Typography>
          ))}
        </Stack>
      )}
    </Section>

    <Section title="Clicks" testId="dsar-package-clicks">
      {pkg.clicks.length === 0 ? (
        <Empty label="No clicks held." />
      ) : (
        <Stack spacing={0.5}>
          {pkg.clicks.map((c) => (
            <Typography key={c.id} variant="body2">
              #{c.id} | merchant {c.merchantId} |{' '}
              {c.converted ? 'converted' : 'no conversion'} |{' '}
              {day(c.occurredAt)}
            </Typography>
          ))}
        </Stack>
      )}
    </Section>

    <Section title="Support tickets" testId="dsar-package-tickets">
      {pkg.tickets.length === 0 ? (
        <Empty label="No tickets held." />
      ) : (
        <Stack spacing={0.5}>
          {pkg.tickets.map((t) => (
            <Typography key={t.id} variant="body2">
              #{t.id} | {t.subject} | {t.status} | {day(t.createdAt)}
            </Typography>
          ))}
        </Stack>
      )}
    </Section>

    <Section title="Welcome bonuses" testId="dsar-package-bonuses">
      {pkg.welcomeBonuses.length === 0 ? (
        <Empty label="No welcome bonuses held." />
      ) : (
        <Stack spacing={0.5}>
          {pkg.welcomeBonuses.map((w) => (
            <Typography key={w.id} variant="body2">
              #{w.id} | {w.amount} {w.currency.toUpperCase()} |{' '}
              {day(w.fundedAt)}
            </Typography>
          ))}
        </Stack>
      )}
    </Section>

    <Section title="Referral" testId="dsar-package-referral">
      <Typography variant="body2">
        {pkg.referral.referredBy
          ? `Referred by member #${pkg.referral.referredBy}`
          : 'Not referred'}
        {pkg.referral.referredAt ? ` | ${day(pkg.referral.referredAt)}` : ''}
      </Typography>
    </Section>

    <Section title="Consent history" testId="dsar-package-consent">
      {pkg.consentHistory.length === 0 ? (
        <Empty label="No consent records held." />
      ) : (
        <Stack spacing={0.5}>
          {pkg.consentHistory.map((c) => (
            <Typography key={c.id} variant="body2">
              #{c.id} | {c.consentType} | {c.action} | {c.source} | policy{' '}
              {c.policyVersion} | {day(c.occurredAt)}
            </Typography>
          ))}
        </Stack>
      )}
    </Section>

    <Section title="Promo redemptions" testId="dsar-package-promo">
      <Typography variant="body2" color="text.secondary">
        Not recorded per member. Promo redemptions are not linked to a member in
        this system, so there is nothing to release here.
      </Typography>
    </Section>
  </Box>
);

export const DsarPackage = ({ subjectUserId }: { subjectUserId: number }) => {
  const { pkg, isLoading } = useDsarExport(subjectUserId);
  if (isLoading || !pkg) return <Loading />;
  return <PackageBody pkg={pkg} />;
};
